import { createRouter, createWebHistory } from "vue-router";
import { nextTick } from "vue";
import { TokenService } from "./services/storage.service";

// Async component imports for code splitting
const DashboardComponent = () => import("./views/Dashboard.vue");
const Login = () => import("./views/Login.vue");
const Account = () => import("./views/Account.vue");
const CreateAccount = () => import("./views/CreateAccount.vue");
const ForgotPassword = () => import("./views/ForgotPassword.vue");
const Maintenance = () => import("./views/Maintenance.vue");
const ConfirmEmail = () => import("./views/ConfirmEmail.vue");
const NotFound = () => import("./views/NotFound.vue");
const ResetPassword = () => import("./views/ResetPassword.vue");
const Inspiration = () => import("./views/Inspiration.vue");
const PitchPortal = () => import("./views/PitchPortal.vue");
const QuickAction = () => import("./views/QuickAction.vue");
import general from "./utils/general";

// Detect local development environment
const isDev = general.isDev();

const DEFAULT_TITLE = "PartnerUp Hub";

const router = createRouter({
	history: createWebHistory(),
	routes: [
		{
			path: "/404",
			name: "NotFound",
			component: NotFound,
			meta: {
				title: "Not Found",
			},
		},
		{
			path: "/quick-action/:action/:actioner/:actionee/:unique",
			name: "quick-action",
			component: QuickAction,
			meta: {
				public: true,
				showHeader: true,
				showFooter: true,
			},
		},
		{
			path: "/login",
			name: "login",
			component: Login,
			meta: {
				public: true, // Allow access to even if not logged in
				onlyWhenLoggedOut: true,
				title: "Login",
			},
		},
		{
			path: "/login/:code/:id",
			name: "login-instant",
			component: Login,
			meta: {
				public: true, // Allow access to even if not logged in
				onlyWhenLoggedOut: true,
				title: "Login",
			},
		},
		{
			path: "/login/:code/:id/:status",
			name: "login-instant-status",
			component: Login,
			meta: {
				public: true, // Allow access to even if not logged in
				onlyWhenLoggedOut: true,
				title: "Login",
			},
		},
		{
			path: "/maintenance",
			name: "maintenance",
			component: Maintenance,
			meta: {
				public: true,
				title: "Maintenance",
			},
		},
		{
			path: "/create-account",
			name: "create-account",
			component: CreateAccount,
			meta: {
				public: true, // Allow access to even if not logged in
				onlyWhenLoggedOut: true,
				title: "Create Account",
			},
		},
		{
			path: "/confirm-email/:pathMatch(.*)*",
			name: "confirm-email",
			component: ConfirmEmail,
			meta: {
				public: true, // Allow access to even if not logged in
				onlyWhenLoggedOut: false,
				title: "Confirm Email",
			},
		},
		{
			path: "/reset-password/:code",
			name: "reset-password",
			component: ResetPassword,
			meta: {
				public: true, // Allow access to even if not logged in
				onlyWhenLoggedOut: true,
				title: "Reset Password",
			},
		},
		{
			path: "/create-password/:code",
			name: "create-password",
			component: ResetPassword,
			meta: {
				public: true, // Allow access to even if not logged in
				onlyWhenLoggedOut: true,
				title: "Reset Password",
			},
		},
		{
			path: "/forgot-password",
			name: "forgot-password",
			component: ForgotPassword,
			meta: {
				public: true, // Allow access to even if not logged in
				onlyWhenLoggedOut: true,
				title: "Forgot Password",
			},
		},
		{
			path: "/setup-password",
			name: "setup-password",
			component: ForgotPassword,
			meta: {
				public: true, // Allow access to even if not logged in
				onlyWhenLoggedOut: true,
				title: "Setup Password",
			},
		},
		{
			path: "/setup-password-direct",
			name: "setup-password-direct",
			component: ForgotPassword,
			meta: {
				public: true, // Allow access to even if not logged in
				onlyWhenLoggedOut: true,
				title: "Setup Password",
			},
		},
		{
			path: "/account/subscriptions/payment-processing/:checkoutSessionId",
			name: "account",
			component: Account,
			meta: {
				title: "Account",
				paymentProcessing: true,
				type: "account",
			},
		},
		{
			path: "/account",
			name: "account-root",
			component: Account,
			meta: {
				title: "Account",
				type: "account",
			},
		},
		{
			path: "/account/:pathMatch(.*)+",
			name: "account-general",
			component: Account,
			meta: {
				title: "Account",
				type: "account",
			},
		},
		{
			path: "/requests",
			name: "requests",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "requests",
			},
		},
		{
			path: "/requests/received",
			name: "requests-received",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "received",
				type: "requests",
			},
		},
		{
			path: "/requests/received/:userBrand/:_id",
			name: "requests-received-detail",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "received",
				type: "request",
			},
		},
		{
			path: "/requests/received/:userBrand/:_id/payment-processing",
			name: "requests-received-payment",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "received",
				paymentProcessing: true,
				type: "request",
			},
		},
		{
			path: "/requests/received/:userBrand/:_id/payment-processing/:checkoutSessionId",
			name: "requests-received-payment-session",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "received",
				paymentProcessing: true,
				type: "request",
			},
		},
		{
			path: "/requests/sent",
			name: "requests-sent",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "sent",
				type: "requests",
			},
		},
		{
			path: "/requests/:userBrand",
			name: "requests-brand",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "requests",
			},
		},
		{
			path: "/requests/:filter/:userBrand",
			name: "requests-filter-brand",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "requests",
			},
		},
		{
			path: "/connections",
			name: "connections",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "connections",
			},
		},
		{
			path: "/connections/connected",
			name: "connections-connected",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "all",
				type: "connections",
			},
		},
		{
			path: "/connections/:userBrand",
			name: "connections-brand",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "connections",
			},
		},
		{
			path: "/connections/:userBrand/:_id",
			name: "connections-brand-detail",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "connections",
			},
		},
		{
			path: "/declines",
			name: "declines",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "declines",
			},
		},
		{
			path: "/declines/out",
			name: "declines-out",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "received",
				type: "declines",
			},
		},
		{
			path: "/declines/in",
			name: "declines-in",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "sent",
				type: "declines",
			},
		},
		{
			path: "/declines/out/:userBrand",
			name: "declines-out-brand",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "received",
				type: "declines",
			},
		},
		{
			path: "/declines/in/:userBrand",
			name: "declines-in-brand",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "sent",
				type: "declines",
			},
		},
		{
			path: "/declines/:userBrand",
			name: "declines-brand",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "declines",
			},
		},
		{
			path: "/inspiration",
			name: "inspiration",
			component: Inspiration,
			meta: {
				showBrands: true,
			},
		},
		{
			path: "/inspiration/ideas",
			name: "inspiration-ideas",
			component: Inspiration,
			meta: {
				showBrands: true,
				tab: "inspiration",
			},
		},
		{
			path: "/inspiration/calendar",
			name: "inspiration-calendar",
			component: Inspiration,
			meta: {
				showBrands: true,
				tab: "calendar",
			},
		},
		{
			path: "/inspiration/member-collabs",
			name: "inspiration-member-collabs",
			component: Inspiration,
			meta: {
				showBrands: true,
				tab: "member-collabs",
			},
		},
		{
			path: "/matches/brand/:_id/payment-processing/:checkoutSessionId",
			name: "matches-brand-payment-processing",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				paymentProcessing: true,
				type: "matches",
			},
		},
		{
			path: "/matches/brand/:_id/payment-processing",
			name: "matches-brand-payment-processing-cancel",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "matches",
				flushCheckoutSessions: true,
			},
		},
		{
			path: "/matches/payment-processing/:checkoutSessionId",
			name: "matches-payment-session",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				paymentProcessing: true,
				type: "matches",
			},
		},
		{
			path: "/matches/payment-processing",
			name: "matches-payment-processing-cancel",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				flushCheckoutSessions: true,
				type: "matches",
			},
		},
		{
			path: "/matches/brands/:brandIds",
			name: "matches-brands",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "matches",
			},
		},
		{
			path: "/matches/brand/:_id",
			name: "matches-brand",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "matches",
			},
		},
		{
			path: "/matches/:userBrand/brand/:_id",
			name: "matches-user-brand-detail",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "matches",
			},
		},
		{
			path: "/matches/potentials",
			name: "matches-potentials",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "received",
				type: "matches",
			},
		},
		{
			path: "/matches/saved",
			name: "matches-saved",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "sent",
				type: "matches",
			},
		},
		{
			path: "/matches/:userBrand",
			name: "matches-user-brand",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "matches",
			},
		},
		{
			path: "/matches/potentials/:userBrand",
			name: "matches-potentials-user-brand",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "received",
				type: "matches",
			},
		},
		{
			path: "/matches/saved/:userBrand",
			name: "matches-saved-user-brand",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				tab: "sent",
				type: "matches",
			},
		},
		{
			path: "/matches/filter/:filterBy/:filterValue",
			name: "matches-filtered",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "matches",
			},
		},
		{
			path: "/matches",
			name: "matches",
			component: DashboardComponent,
			meta: {
				showBrands: true,
				type: "matches",
			},
		},
		{
			path: "/brand/:_id",
			name: "dashboard-brand",
			component: DashboardComponent,
			meta: {
				brand: true,
				showBrands: true,
				type: "brand",
			},
		},
		{
			path: "/brand/:userBrand/:_id",
			name: "dashboard-brand-detail",
			component: DashboardComponent,
			meta: {
				brand: true,
				showBrands: true,
				type: "brand",
			},
		},
		{
			path: "/opportunities",
			name: "pitch",
			component: PitchPortal,
			meta: {
				type: "pitch",
				viewKey: "opportunities",
			},
		},
		{
			path: "/opportunities/jump/:_id",
			name: "pitch-jump",
			component: PitchPortal,
			meta: {
				jump: true,
				type: "pitch",
			},
		},
		{
			path: "/opportunities/:_id/payment-processing",
			name: "pitch-focus",
			component: PitchPortal,
			meta: {
				paymentProcessing: true,
				type: "pitch",
			},
		},
		{
			path: "/opportunities/:_id/payment-processing/:checkoutSessionId",
			name: "pitch-payment-session2",
			component: PitchPortal,
			meta: {
				paymentProcessing: true,
				type: "pitch",
			},
		},
		{
			path: "/opportunities/payment-processing",
			name: "pitch-payment",
			component: PitchPortal,
			meta: {
				paymentProcessing: true,
				type: "pitch",
			},
		},
		{
			path: "/opportunities/new",
			name: "pitch-new",
			component: PitchPortal,
			meta: {
				newFeature: true,
				type: "pitch",
			},
		},
		{
			path: "/opportunities/payment-processing/:checkoutSessionId",
			name: "pitch-payment-session",
			component: PitchPortal,
			meta: {
				paymentProcessing: true,
				type: "pitch",
			},
		},
		{
			path: "/opportunities/:_id",
			name: "pitch-focus2",
			component: PitchPortal,
			meta: {
				type: "pitch",
				viewKey: "opportunities",
			},
		},
		{
			path: "/pitch/:pathMatch(.*)*",
			redirect: (to) => {
				const suffix = Array.isArray(to.params.pathMatch) ? to.params.pathMatch.join("/") : to.params.pathMatch || "";
				return {
					path: suffix ? `/opportunities/${suffix}` : "/opportunities",
					query: to.query,
					hash: to.hash,
				};
			},
		},
		{
			path: "/stop-welcome-emails/:email",
			name: "dashboard-stop-welcome",
			component: DashboardComponent,
			meta: {
				stopWelcomeEmails: true,
			},
		},
		{
			path: "/payment-processing/:checkoutSessionId",
			name: "dashboard-payment-session",
			component: DashboardComponent,
			meta: {
				paymentProcessing: true,
				type: "pitch",
			},
		},
		{
			path: "/payment-processing",
			name: "dashboard-payment",
			component: DashboardComponent,
			meta: {
				paymentProcessing: true,
				type: "pitch",
			},
		},
		{
			path: "/",
			name: "dashboard",
			component: DashboardComponent,
		},
		// Catch-all route for 404s - must be last
		/*
		{
			path: "/:pathMatch(.*)*",
			redirect: "/404",
		},*/
	],
});
export default router;

router.beforeEach((to, from, next) => {
	const isPublic = to.matched.some((record) => record.meta.public);
	const onlyWhenLoggedOut = to.matched.some((record) => record.meta.onlyWhenLoggedOut) && to.name !== "login-instant";
	const loggedIn = !!TokenService.getToken();
	const devQueryEmail = Array.isArray(to.query.email) ? to.query.email[0] : to.query.email;
	const hasDevLoginEmailOverride = isDev && to.name === "login" && typeof devQueryEmail === "string" && devQueryEmail.trim() !== "";
	if (!isPublic && !loggedIn) {
		return next({
			path: "/login/",
			query: { redirect: to.fullPath }, // Store the full path to redirect the user to after login
		});
	}

	// Do not allow user to visit login page or register page if they are logged in
	if (loggedIn && onlyWhenLoggedOut && !hasDevLoginEmailOverride) {
		return next("/");
	}

	next();
});

router.afterEach((to) => {
	if (typeof window !== "undefined" && typeof window.gtag !== "undefined" && !to.meta.noTrack) {
		window.gtag("config", "G-F9NT3JPYVH", {
			page_path: to.fullPath,
			app_name: DEFAULT_TITLE,
			send_page_view: true,
		});
	}
	nextTick(() => {
		document.title = to.meta.title ? DEFAULT_TITLE + ": " + to.meta.title : DEFAULT_TITLE;
	});
});
